import {merchantLocalStorage} from "../modules/merchant-state/MerchantStorage.js";
import {MERCHANT_STORAGE_KEYS} from "../Constants.js";
import {Dispatcher} from "../../stem-core/base/Dispatcher";
import {isString} from "../../stem-core/base/Utils.js";


const PAGE_VISITS_MAX_ENTRIES = 50;
let lastPageAccessed: string | null = null;
let watcherInstalled = false;
let includeHashInPath = false;
const pageAccessDispatcher = new Dispatcher();

// Hash routers (e.g. /#/article/2) keep the same pathname for every route. Merchants on such a
// router opt in so that each route is counted as a distinct page. Off by default so existing
// path-based merchants' visit keys are unchanged.
export function setHashRoutingEnabled(enabled: boolean) {
    includeHashInPath = enabled;
}

// The path the current route should be matched against. On a hash router the route lives in the hash, so
// leaving it out would make every route look like the same page to the path conditions.
export function getCurrentPath() {
    return self.location.pathname + (includeHashInPath ? self.location.hash : "");
}

export function normalizePath(url: string | URL) {
    if (isString(url)) {
        url = new URL(url, self.location.href);
    }
    // TODO This is from Darius, why do we care about normalizetion
    const path = url.pathname + (url.pathname.endsWith("/") ? "" : "/");
    return includeHashInPath ? path + url.hash : path;
}

export function isPathEqual(a: string | URL, b: string | URL) {
    return normalizePath(a) === normalizePath(b);
}

interface PageVisitRecord {
    firstVisit: number;
    lastVisit: number;
}

export function loadPageVisitsMap(): Map<string, PageVisitRecord> {
    const initialState = merchantLocalStorage.getItem(MERCHANT_STORAGE_KEYS.pageVisits);
    return new Map(initialState?.v || []);
}

export function updatePageVisit(url: string | URL, metadata: Record<string, any>): [PageVisitRecord, Map<string, PageVisitRecord>] {
    let pageVisitsMap = loadPageVisitsMap();
    const path = normalizePath(url);

    const now = Date.now();
    const currentVisit = {
        firstVisit: now, // To be overwritten if exists
        ...pageVisitsMap.get(path),
        ...metadata,
        lastVisit: now,
    };

    // Add to the set and maybe clean it if there are too many entries
    pageVisitsMap.set(path, currentVisit);

    // Truncate if there are too many.
    if (pageVisitsMap.size > PAGE_VISITS_MAX_ENTRIES) {
        const newEntries = [...pageVisitsMap]
            .sort((a, b) => b[1].lastVisit - a[1].lastVisit) // sort descending by timestamp
            .slice(0, PAGE_VISITS_MAX_ENTRIES); // keep the most recent

        pageVisitsMap = new Map(newEntries);
    }
    merchantLocalStorage.setItem(MERCHANT_STORAGE_KEYS.pageVisits, {
        v: [...pageVisitsMap],
    });
    return [currentVisit, pageVisitsMap];
}

export function isURLViewed(url: string) {
    const visitedPages = [
        self.location.href, // normalizePath below applies the same hash rule getCurrentPath does
        ...loadPageVisitsMap().keys()
    ]

    for (const visitedPath of visitedPages) {
        if (isPathEqual(url, visitedPath)) {
            return true;
        }
    }
    return false;
}

function checkUrlChanged() {
    const currentUrl = self.location.href;
    if (currentUrl !== lastPageAccessed) {
        lastPageAccessed = currentUrl;
        pageAccessDispatcher.dispatch(currentUrl);
    }
}

// Makes the SDK aware of SPA navigations. Installed once; idempotent.
function installPageAccessWatcher() {
    if (watcherInstalled) {
        return;
    }
    watcherInstalled = true;
    lastPageAccessed = self.location.href;

    // Hook the History API so client-side navigations dispatch synchronously, instead of waiting
    // up to 200ms for the poll below. We wrap (call the original first) so we don't break a
    // merchant's own router, and use addEventListener for popstate because stem-core's Router
    // already owns window.onpopstate.
    try {
        const history = self.history as any;
        for (const method of ["pushState", "replaceState"]) {
            const original = history[method];
            if (typeof original !== "function") {
                continue;
            }
            history[method] = function (...args: any[]) {
                const result = original.apply(this, args);
                checkUrlChanged();
                return result;
            };
        }
        self.addEventListener("popstate", checkUrlChanged);
        self.addEventListener("hashchange", checkUrlChanged);
    } catch (error) {
        console.error("[Blink] Failed to instrument the History API for page access tracking", error);
    }

    // Fallback for routers that change the URL without going through the History API.
    setInterval(checkUrlChanged, 200);
}

// Will immediately call the function with the current URL
export function onCurrentAndFuturePageAccess(callback: (url: string | null) => void) {
    installPageAccessWatcher();
    callback(lastPageAccessed);
    return pageAccessDispatcher.addListener(callback);
}

export function dispatchPageAccess(url: string) {
    pageAccessDispatcher.dispatch(url);
}

export function markURLVisited(url: string, metadata?: Record<string, any>) {
    updatePageVisit(url, metadata);
    dispatchPageAccess(url);
}
